import React, { useEffect, useRef, useState } from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { Link, NavLink, Outlet } from "react-router-dom-v5-compat";

import {
  SearchField,
  LinkButton,
  Price,
  RecordFeatureView,
  PriceLine,
} from "./Atoms";
import { OrderedOrderItems } from "./Pages/Cart/OrderedOrderItem";
import { PendingCartItem } from "./Pages/Cart/PendingCartItem";
import { getCartTotal } from "./Pages/Cart/selectors";
import OnlineOrderingDisabled from "./Pages/OnlineOrderingSchedule/OnlineOrderingDisabled";
import { SearchResults } from "./Pages/Sections/Section";
import { useSession } from "./Pages/Session/SessionContext";
import { getIsWishlist } from "./Pages/Session/sessionSelectors";
import { VenueOnlineConfigQueries } from "./Pages/VenueOnlineConfig/queries";
import { useVenue } from "./VenueContext";
import { getActiveSections, getPromotions } from "./menuSelectors";
import { useFetchMenu } from "./queries";
import type { Venue, Menu, Section } from "./schema";
import { getVenueHasMobileOrdering } from "./venueSelectors";

interface MainPageProps {
  smallView?: boolean;
  isRootPage?: boolean;
}
const hasLogoImage = ({ logoImageUrl }: Venue) =>
  logoImageUrl && logoImageUrl != "/assets/missing.png";
export const LogoImage = () => {
  const venue = useVenue();

  if (!hasLogoImage(venue)) {
    return null;
  }
  return (
    <div className="aspect-h-1 aspect-w-1">
      <img
        className={classNames(
          "rounded-venue-brand bg-venue-primary object-cover",
        )}
        src={venue.logoImageUrl}
      />
    </div>
  );
};

interface SectionBoxProps {
  name: string;
  forceMobileLayout?: boolean;
  isActive?: boolean;
  sectionImageUrl?: string | null;
}

export const SectionBox = ({
  forceMobileLayout,
  sectionImageUrl,
  name,
  isActive,
}: SectionBoxProps) => {
  const hasLongWord = name.split(" ").some((word) => word.length > 10);

  return (
    <div
      className={classNames("space-y-8 rounded-venue-brand", {
        "md:group is-active md:bg-venue-highlight md:text-venue-secondary":
          isActive,
        "md:p-8": !forceMobileLayout,
      })}
    >
      <div className="h-[72px] w-[72px]">
        {sectionImageUrl ? (
          <img
            className="h-full w-full rounded-venue-brand object-cover"
            src={sectionImageUrl}
          />
        ) : (
          <div className="text-sm flex h-full items-center justify-center rounded-venue-brand bg-venue-primary text-center font-medium text-venue-secondary group-[.is-active]:bg-venue-secondary group-[.is-active]:text-venue-primary">
            {name}
          </div>
        )}
      </div>
      {sectionImageUrl && (
        <div
          className={classNames(
            "w-[72px] flex-1 overflow-clip text-ellipsis text-center font-medium",
            {
              "text-xs": hasLongWord,
              "text-sm": !hasLongWord,
            },
          )}
        >
          {name}
        </div>
      )}
    </div>
  );
};

export const SectionsGrid = ({
  menu,
  forceMobileLayout,
}: {
  menu: Menu;
  forceMobileLayout?: boolean;
  gridLayout?: boolean;
}) => {
  const sections = getActiveSections(menu);
  const { t } = useTranslation("onsite");
  const promotions = getPromotions(menu);
  const promotionSection: Section = {
    uuid: "promotions",
    name: t("promotions.title"),
    pricingType: "simple",
    items: promotions,
  };
  const vouchersEnabled = useSuspenseQuery({
    ...VenueOnlineConfigQueries.current(),
    select: (data) => data.onlineVoucherSales,
  }).data;
  return (
    <div
      className={classNames(
        "flex min-w-0 max-w-full space-x-16 overflow-x-auto",
        { "md:-mx-8 md:space-x-12": !forceMobileLayout },
      )}
    >
      {promotions.length > 0 && (
        <NavLink
          to={"/"}
          // eslint-disable-next-line react/no-children-prop
          children={({ isActive }) => (
            <SectionBox
              isActive={isActive}
              forceMobileLayout={forceMobileLayout}
              key={promotionSection.uuid}
              name={promotionSection.name}
              sectionImageUrl={
                promotions.find((p) => p.heroImageThumbnailUrl)
                  ?.heroImageThumbnailUrl
              }
            />
          )}
        />
      )}
      {vouchersEnabled && (
        <NavLink
          to={"vouchers"}
          // eslint-disable-next-line react/no-children-prop
          children={({ isActive }) => (
            <SectionBox
              isActive={isActive}
              forceMobileLayout={forceMobileLayout}
              key={"vouchers"}
              name={t("mainPage.vouchers")}
            />
          )}
        />
      )}

      {sections.map((section) => (
        <NavLink
          key={section.uuid}
          to={section.uuid}
          // eslint-disable-next-line react/no-children-prop
          children={({ isActive }) => (
            <SectionBox
              isActive={isActive}
              forceMobileLayout={forceMobileLayout}
              key={section.uuid}
              name={section.name}
              sectionImageUrl={
                section.items.find((i) => i.heroImageThumbnailUrl)
                  ?.heroImageThumbnailUrl
              }
            />
          )}
        />
      ))}
    </div>
  );
};

export const VenueWelcome = () => {
  const venue = useVenue();

  return (
    <div className="space-y-12 md:flex md:flex-1 md:items-center md:space-x-16 md:space-y-0">
      {hasLogoImage(venue) && (
        <div className="mx-auto w-96 md:mx-0 md:text-right">
          <Link
            to={`/`}
            className="mx-auto block w-full max-w-[96px] space-y-8 text-center"
          >
            <LogoImage />
          </Link>
        </div>
      )}
      <div className="space-y-16 md:flex-1">
        <div className="text-center text-xl font-extrabold text-venue-primary">
          <Trans
            i18nKey="mainPage.welcomeToVenue"
            values={{ venueName: venue.name }}
            shouldUnescape
          />
        </div>
        <div className="text-sm mx-auto whitespace-pre-wrap font-normal">
          {venue.venueBrand.description}
        </div>
      </div>
    </div>
  );
};

const MenuFilter = ({
  searchTerm,
  onSearchChange,
}: {
  searchTerm?: string;
  onSearchChange: (newTerm: string) => void;
}) => {
  return (
    <div className="space-y-8">
      <SearchField searchTerm={searchTerm} onChange={onSearchChange} />
    </div>
  );
};

const MainPage = ({ isRootPage }: MainPageProps) => {
  const menu = useFetchMenu();

  const isLayoutPage = !isRootPage;
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const onSearchChange = (newTerm: string) => {
    setSearchTerm(newTerm);
  };

  return (
    <RecordFeatureView featureName="Online / Menu">
      <div
        className={classNames("relative space-x-48 pb-96 md:flex md:py-32", {
          "py-16": isRootPage,
        })}
      >
        <div className={classNames("md:flex-1", { "space-y-24": isRootPage })}>
          <div
            className={classNames("flex-1 space-y-24 md:flex md:space-x-32", {
              "hidden md:flex": isLayoutPage,
            })}
          >
            <div className="space-y-24 md:w-0 md:min-w-0 md:flex-1 md:space-y-32">
              {!searchTerm && (
                <>
                  <VenueWelcome />
                  <SectionsGrid menu={menu} />
                  <OnlineOrderingDisabled />
                </>
              )}
              {isRootPage && (
                <div className="md:hidden">
                  <MenuFilter
                    searchTerm={searchTerm}
                    onSearchChange={onSearchChange}
                  />
                </div>
              )}
            </div>
          </div>
          {searchTerm ? (
            <SearchResults
              resetSearch={() => setSearchTerm("")}
              menu={menu}
              searchTerm={searchTerm}
            />
          ) : (
            <Outlet />
          )}
        </div>
        <div
          className={classNames(
            "hidden max-h-screen min-h-0 space-y-24 self-start py-32 md:sticky md:top-0 md:block md:h-screen md:max-w-sm md:flex-1 md:flex-col",
            {},
          )}
        >
          <div className="flex-none">
            <MenuFilter
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
            />
          </div>
          <div className="flex-1">
            <SideBarCart />
          </div>
        </div>
      </div>
    </RecordFeatureView>
  );
};

export default MainPage;

const ScrollIntoView = ({
  parentRef,
  children,
}: {
  parentRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref?.current && parentRef?.current) {
      const parentNode = parentRef?.current;
      parentNode.scrollTop = ref.current.offsetTop;
    }
  }, []);
  return (
    <div
      className="scroll-smooth"
      style={{ scrollMarginTop: "-400px" }}
      ref={ref}
    >
      {children}
    </div>
  );
};
const SideBarCart = () => {
  const { t } = useTranslation("onsite");
  const isWishList = getIsWishlist(useSession(), useVenue());
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const { cart } = useSession();
  const totalOrderPrice = getCartTotal(cart);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  return (
    <div className="">
      <div
        ref={scrollContainerRef}
        className="h-[85vh] min-h-0 space-y-24 overflow-y-auto scroll-smooth rounded-venue-brand border-[6px] border-venue-tertiary px-12"
      >
        <h3 className="sticky top-0 z-10 bg-venue-secondary py-12 text-center text-2xl font-bold">
          {orderingEnabled ? t("cart.yourOrder") : t("cart.yourWishlist")}
        </h3>
        <div className="space-y-12">
          <div className="-mx-2 space-y-12 overflow-y-auto p-2">
            {cart.items.length > 0 ? (
              cart.items.map((cartItem) => (
                <ScrollIntoView
                  parentRef={scrollContainerRef}
                  key={cartItem.id}
                >
                  <PendingCartItem key={cartItem.id} cartItem={cartItem} />
                </ScrollIntoView>
              ))
            ) : (
              <div className="py-24 text-center text-lg font-medium">
                {t("cart.noPendingOrders")}
              </div>
            )}
          </div>
        </div>
        <OrderedOrderItems />

        <div className="sticky bottom-0 space-y-16 bg-venue-secondary py-12">
          <PriceLine
            label={t("cart.total")}
            value={<Price showCurrency value={totalOrderPrice} />}
          />
          <LinkButton primary to="/your-order/pending">
            {isWishList ? t("mainPage.yourWishlist") : t("mainPage.yourOrder")}
          </LinkButton>
        </div>
      </div>
    </div>
  );
};
